import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle } from 'lucide-react';
import { SubmissionPipelinePanel } from '@/components/SubmissionPipelinePanel';
import ProjectHierarchyPicker, { ShowSelectedBanner } from '@/components/ProjectHierarchyPicker';
import useActiveProject from '@/hooks/useActiveProject';

export default function AllotmentPage() {
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useActiveProject(user?.id);

  useEffect(() => {
    api.get('/projects').then(r => {
      const list = r.data || [];
      setProjects(list);
      // Self-heal: pick first project only if no project is currently active OR
      // the persisted one no longer exists (deleted in another tab).
      if (list.length && (!selectedProject || !list.find(p => p.id === selectedProject))) {
        setSelectedProject(list[0].id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    if (!selectedProject) return;
    api.get(`/allotment/${selectedProject}`).then(r => setData(r.data)).catch(console.error);
  }, [selectedProject, api]);

  if (!data) return <div className="flex items-center justify-center h-64"><div className="animate-pulse-slow text-zinc-600 text-sm">Loading...</div></div>;

  const { summary, workforce } = data;
  const vendors = workforce.vendors || [];

  return (
    <div className="space-y-4" data-testid="allotment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Allotment Engine</h1>
          <p className="text-xs text-zinc-500 mt-1">Assign pipeline stages to the right resources</p>
        </div>
        <ProjectHierarchyPicker projects={projects} value={selectedProject}
          onChange={setSelectedProject} data-testid="allotment-project-select" />
      </div>

      <ShowSelectedBanner
        project={projects.find(p => p.id === selectedProject)}
        projects={projects}
        onPickEpisode={setSelectedProject}
        testId="allotment-show-banner" />

      {/* Summary */}
      <div className="grid grid-cols-5 gap-2">
        {[
          { l: 'Chapters', v: summary.total_chapters, c: 'text-white' },
          { l: 'Total Assets', v: summary.total_items, c: 'text-blue-400' },
          { l: 'Assigned', v: summary.total_assigned, c: 'text-emerald-400' },
          { l: 'Unassigned', v: summary.total_unassigned, c: 'text-red-400' },
          { l: 'Coverage', v: `${summary.assignment_rate}%`, c: 'text-amber-400' },
        ].map(s => (
          <div key={s.l} className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
            <p className={`text-lg font-heading font-bold ${s.c}`}>{s.v}</p>
            <p className="text-[9px] text-zinc-500">{s.l}</p>
          </div>
        ))}
      </div>

      {/* Workforce bar */}
      <div className="flex gap-4 text-[10px] items-center">
        <span className="text-zinc-500">Workforce:</span>
        <span className="text-blue-400">{workforce.internal?.length || 0} Internal</span>
        <span className="text-amber-400">{workforce.freelancers?.length || 0} Freelancers</span>
        <span className="text-violet-400">{vendors.length} Vendors</span>
        {workforce.total_available === 0 && <span className="text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No workforce — add in HR first</span>}
      </div>

      {/* SUBMISSION PIPELINE is THE one place for all allotment.
          Founder ask (May 14, 2026): the legacy top-row pipeline tabs
          (CBA · ASSET BUILD · RIGGING · ANIMATION · LIGHTING · COMPING)
          duplicated the same stages already inside the Submission Pipeline
          below — "two CBAs" confused the team. Removed entirely. The
          single Submission Pipeline panel handles all 13 departments,
          and the CBA chip inside it surfaces the full CBA Allotment
          Console (auto-scan + per-shot allotment). */}
      <SubmissionPipelinePanel api={api} projectId={selectedProject} />
    </div>
  );
}
